import type {
  AdminAuditRow,
  AdminBusinessDetail,
  AdminGroup,
  AdminPackage,
  AdminTeamMember,
} from "@teamna/shared";
import { businessRoleWords } from "@teamna/shared";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  ErrorState,
  PageHeader,
  SkeletonList,
  StatTile,
  Tabs,
  panelId,
  tabId,
  type DataTableColumn,
} from "@teamna/ui/components/index.ts";
import { setActingAs } from "../lib/act-as.ts";
import { useAdminBusiness } from "../lib/cc-api.ts";
import { formatDateTime, formatMoney } from "@teamna/web-client/lib/format.ts";
import { countText, setupText, sportName } from "./format.ts";
import { BillingCard } from "./billing-card.tsx";
import { DeleteBusinessCard } from "./delete-business.tsx";
import { SignupSourceCard } from "./signup-source.tsx";
import { RunsOnTeamnaCard } from "./runs-on-teamna-card.tsx";
import { AiAssistantCard } from "./ai-assistant-card.tsx";

/**
 * One business, from Teamna's side (web-app-v2.md section 5, moved to Control Center).
 *
 * The tiles answer "is this business alive and set up"; the tabs hold the rows behind them. The main
 * action is "Act as this business", which opens the owner office of this business inside Control
 * Center (control-center-split.md, section 4). The danger zone at the end deletes the business.
 * Times are in the business timezone.
 */

const BACK = { to: "/businesses", label: "Businesses" };

const ROLE_NAMES: Record<AdminTeamMember["role"], string> = businessRoleWords;

type TabId = "team" | "groups" | "packages" | "audit";

const TEAM_COLUMNS: DataTableColumn<AdminTeamMember>[] = [
  {
    id: "name",
    header: "Name",
    sortValue: (row) => row.name,
    cell: (row) => row.name ?? <span className="text-ink-3">No name</span>,
  },
  { id: "role", header: "Role", sortValue: (row) => row.role, cell: (row) => ROLE_NAMES[row.role] },
];

const GROUP_COLUMNS: DataTableColumn<AdminGroup>[] = [
  { id: "name", header: "Name", sortValue: (row) => row.name, cell: (row) => row.name },
  { id: "kind", header: "Kind", sortValue: (row) => row.kind, cell: (row) => sportName(row.kind) },
  {
    id: "slots",
    header: "Slots per week",
    align: "end",
    sortValue: (row) => row.slotsPerWeek,
    cell: (row) => <span className="tabular-nums">{row.slotsPerWeek}</span>,
  },
];

const PACKAGE_COLUMNS: DataTableColumn<AdminPackage>[] = [
  { id: "name", header: "Name", sortValue: (row) => row.name, cell: (row) => row.name },
  {
    id: "sessions",
    header: "Sessions",
    align: "end",
    sortValue: (row) => row.sessions,
    cell: (row) => <span className="tabular-nums">{row.sessions}</span>,
  },
  {
    id: "price",
    header: "Price",
    align: "end",
    sortValue: (row) => row.priceFils,
    cell: (row) => <span className="tabular-nums">{formatMoney(row.priceFils)}</span>,
  },
  {
    id: "active",
    header: "Status",
    sortValue: (row) => (row.active ? 0 : 1),
    cell: (row) =>
      row.active ? <Badge tone="confirmed">Active</Badge> : <Badge tone="quiet">Hidden</Badge>,
  },
];

function auditColumns(timezone: string): DataTableColumn<AdminAuditRow>[] {
  return [
    {
      id: "when",
      header: "When",
      sortValue: (row) => Date.parse(row.createdAt),
      cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.createdAt, timezone)}</span>,
    },
    {
      id: "action",
      header: "Action",
      sortValue: (row) => row.action,
      cell: (row) => <code className="text-[13px]">{row.action}</code>,
    },
    {
      id: "actor",
      header: "Actor",
      sortValue: (row) => row.actorName ?? row.actorStaffEmail,
      // A Control Center change has no person in the business, only the staff email.
      cell: (row) => row.actorName ?? row.actorStaffEmail ?? <span className="text-ink-3">System</span>,
    },
    { id: "entity", header: "Entity", sortValue: (row) => row.entity, cell: (row) => row.entity },
  ];
}

export function AdminBusinessDetailScreen() {
  const { id } = useParams({ from: "/staff/businesses/$id" });
  const business = useAdminBusiness(id);

  if (business.isLoading) {
    return (
      <>
        <PageHeader title="Business" back={BACK} />
        <output aria-label="Loading the business" className="block">
          <SkeletonList rows={3} />
        </output>
      </>
    );
  }

  if (!business.data) {
    return (
      <>
        <PageHeader title="Business" back={BACK} />
        <ErrorState
          error={business.error ?? { code: "NOT_FOUND" }}
          onRetry={() => void business.refetch()}
        />
      </>
    );
  }

  return <BusinessDetail business={business.data} />;
}

function BusinessDetail({ business }: { business: AdminBusinessDetail }) {
  const [tab, setTab] = useState<TabId>("team");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasOwner = business.team.some((member) => member.role === "owner");

  const actAs = () => {
    setActingAs({ businessId: business.id, businessName: business.name });
    // Owner data from a previous act-as must never paint for this business.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "cc" });
    void navigate({ to: "/owner/home" });
  };
  const payments = business.paymentsThisMonth;
  const requests = business.openPaymentRequests;

  const owner = business.ownerName ?? business.ownerIdentifier;
  const description = [sportName(business.sport), business.slug, owner ? `Owner ${owner}` : "No owner"]
    .join(" · ");

  return (
    <>
      <PageHeader
        title={business.name}
        description={description}
        back={BACK}
        actions={
          <Button onClick={actAs} disabled={!hasOwner} title={hasOwner ? undefined : "This business has no owner to act as."}>
            Act as this business
          </Button>
        }
      />

      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatTile label="Members active" value={business.membersActive} />
        <StatTile label="Participants" value={business.participants} />
        <StatTile label="Staff" value={business.staffCount} />
        <StatTile label="Setup" value={setupText(business.setupDone, business.setupTotal)} />
        <StatTile
          label="Card payments this month"
          value={formatMoney(payments.cardTotalFils)}
          delta={countText(payments.cardCount, "payment", "payments")}
        />
        <StatTile
          label="Open payment requests"
          value={requests.count}
          delta={`${formatMoney(requests.totalFils)} in total`}
        />
      </div>

      {business.demoExpiresAt ? null : <SignupSourceCard key={business.id} businessId={business.id} source={business.signupSource} />}

      {business.demoExpiresAt ? null : <BillingCard businessId={business.id} timezone={business.timezone} />}
      <RunsOnTeamnaCard businessId={business.id} />
      <AiAssistantCard businessId={business.id} timezone={business.timezone} />

      <Tabs
        label="Business details"
        selectedId={tab}
        onSelect={(next) => setTab(next as TabId)}
        tabs={[
          { id: "team", label: "Team", count: business.team.length },
          { id: "groups", label: "Groups", count: business.groups.length },
          { id: "packages", label: "Packages", count: business.packages.length },
          { id: "audit", label: "Audit", count: business.audit.length },
        ]}
      />

      <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
        {tab === "team" ? (
          <DataTable
            columns={TEAM_COLUMNS}
            rows={business.team}
            rowKey={(row) => row.userId}
            countLabel={(count) => countText(count, "person", "people")}
            caption="Team"
            empty={{ title: "No team", message: "Nobody runs this business yet." }}
          />
        ) : tab === "groups" ? (
          <DataTable
            columns={GROUP_COLUMNS}
            rows={business.groups}
            rowKey={(row) => row.id}
            countLabel={(count) => countText(count, "group", "groups")}
            caption="Groups"
            empty={{ title: "No groups", message: "This business has no groups yet." }}
          />
        ) : tab === "packages" ? (
          <DataTable
            columns={PACKAGE_COLUMNS}
            rows={business.packages}
            rowKey={(row) => row.id}
            countLabel={(count) => countText(count, "package", "packages")}
            caption="Packages"
            empty={{ title: "No packages", message: "This business sells no packages yet." }}
          />
        ) : (
          <DataTable
            columns={auditColumns(business.timezone)}
            rows={business.audit}
            rowKey={(row) => row.id}
            defaultSort={{ id: "when", direction: "desc" }}
            countLabel={(count) => `Last ${countText(count, "row", "rows")}`}
            caption="Audit"
            empty={{ title: "No audit rows", message: "Nothing has happened in this business yet." }}
          />
        )}
      </div>

      <DeleteBusinessCard business={business} />
    </>
  );
}
